import { CheckCircle, CircleOutlined } from '@mui/icons-material';
import { Box, Typography, Divider, FormControlLabel, Checkbox, styled, Grid } from '@mui/material';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import SummaryCard from '@acorn-portal/shared/src/components/card/SummaryCard';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { FC, useEffect, useState } from 'react';
import { CvMtaModel } from '@acorn-portal/shared/src/services/api';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import { useNavigate } from 'react-router';
import useReferenceData from '@acorn-portal/shared/src/hooks/useReferenceData';
import { ListNames } from '@acorn-portal/shared/src/store/referenceData.slice';
import { flipToPositive, formatPounds, isTelematics } from '@acorn-portal/shared/src/utils';
import { useDiscardMta } from '@acorn-portal/shared/src/hooks/use-discard-mta';
import { setRoutingOptions } from '@acorn-portal/shared/src/store/midTermAdjustmentRatingResponse.slice';
import Price from '@acorn-portal/shared/src/components/display/price';
import DateDisplay from '@acorn-portal/shared/src/components/display/date';
import Vehicle from '@acorn-portal/shared/src/components/display/vehicle';
import AddressDisplay from '@acorn-portal/shared/src/components/display/address';
import Drivers from '@acorn-portal/shared/src/components/display/drivers';
import UsageAndMilage from '@acorn-portal/shared/src/components/display/usage-and-milage';
import Endorsements from '@acorn-portal/shared/src/components/display/endorsements';
import PriceBreakdown from '@acorn-portal/shared/src/components/display/price-breakdown';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import DriverExcess from '@acorn-portal/shared/src/components/display/driver-excess';

import { IMidTermAdjustmentState } from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import isEqual from '@acorn-portal/shared/src/utils/isEqual';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import { findPaymentOptionHasOneOfTypes, hasPaymentOptionOfType } from '@acorn-portal/shared/src/pages/mta/utils';
import { PaymentType } from '@acorn-portal/shared/src/utils/types/payment';
import PostalDocuments from '@acorn-portal/shared/src/components/content/postal-documents';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';
import { PricingGuidanceAlert } from './components/pricing-guidance-alert';

const CopyText = styled(Typography)({
	textAlign: 'left',
	marginBottom: 4,
	marginTop: 4,
});

interface ISummaryPriceProps {
	totalAmountPayable?: number;
	mtaStartDate: string;
	fee?: number;
	product: Product;
}

const SummaryPrice = ({ totalAmountPayable, mtaStartDate, fee, product }: ISummaryPriceProps) => {
	return (
		<Grid
			container
			rowSpacing={2}
			columnSpacing={2}
			gridAutoRows='1fr'
			sx={{ flexDirection: { xs: 'column', md: 'row' }, mb: 2 }}
		>
			<Grid size={{ sm: 6 }}>
				<Box
					sx={{
						border: 1,
						borderColor: 'grey.300',
						p: 2,
						borderRadius: 2,
						height: '100%',
						width: '100%',
					}}
				>
					<DateDisplay date={mtaStartDate} description='This change starts' />
				</Box>
			</Grid>
			<Grid size={{ sm: 6 }}>
				<Box
					sx={(theme) => ({
						border: 1,
						borderColor: 'grey.300',
						p: 2,
						borderRadius: 2,
						height: '100%',
						textAlign: 'right',
						[theme.breakpoints.down('md')]: {
							textAlign: 'left',
						},
					})}
				>
					{totalAmountPayable !== undefined && totalAmountPayable >= 0 && (
						<Price color='primary.main' value={totalAmountPayable} description='Amount Payable' />
					)}
					{totalAmountPayable !== undefined && totalAmountPayable < 0 && (
						<Price color='primary.main' value={flipToPositive(totalAmountPayable)} description='Refund Amount' />
					)}
					<PriceBreakdown price={totalAmountPayable} fee={fee} product={product} />
				</Box>
			</Grid>
		</Grid>
	);
};

const RemoveUnusedProperties = (object: IMidTermAdjustmentState) => {
	const newObject: CvMtaModel = {
		transactionId: object.transactionId,
		vehicleDetails: {
			...object.vehicleDetails,
		},
		policyDetails: {
			...object.policyDetails,
		},
		drivers: [...object.drivers],
		invalidMtaStartDate: object.invalidMtaStartDate,
	};

	return newObject;
};

const MtaSummaryScreenPage: FC = () => {
	const [confirmChanges, setConfirmChanges] = useState(false);
	const [open, toggleModal] = useState(false);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { getReferenceDataValue } = useReferenceData();
	const state = useAppSelector((redux) => redux.midTermAdjustment);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const mtaResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);
	const { track } = useTrack();

	const oldMTA = useAppSelector((redux) => redux.midTermAdjustmentAPI);
	const MtaChangesMade = isEqual(oldMTA, RemoveUnusedProperties(state));
	const { discardLoading, handleDiscardMta } = useDiscardMta();

	useEffect(() => {
		if (mtaResponse.paymentOptions.length === 0) {
			navigate(ROUTE_PATHS.error);
		}
	}, []);

	useEffect(() => {
		track(`MTA Summary Page Viewed`);
	}, []);

	let price = findPaymentOptionHasOneOfTypes(mtaResponse.paymentOptions, [
		PaymentType.SINGLE_PAYMENT_PAYMENT_CARD_AP,
		PaymentType.SINGLE_PAYMENT_PAYMENT_CARD_RP,
		PaymentType.INSTALMENT_ADJUSTMENT_SPREAD_EVENLY,
		PaymentType.INSTALMENT_ADJUSTMENT_NEXT_INSTALMENT,
		PaymentType.SINGLE_PAYMENT_DIRECT_CREDIT_RP,
	]);

	if (price?.totalAmountPayable && price?.totalAmountPayable < 0) {
		const financePrice = findPaymentOptionHasOneOfTypes(mtaResponse.paymentOptions, [
			PaymentType.INSTALMENT_ADJUSTMENT_SPREAD_EVENLY,
			PaymentType.INSTALMENT_ADJUSTMENT_NEXT_INSTALMENT,
		]);

		if (financePrice) {
			price = financePrice;
		}
	}

	const handleRoutingToPaymentOptions = () => {
		let isReturnPremium = false;
		let isCardPaymentAvailable = false;
		let isFinanceAvailable = false;

		if (price?.totalAmountPayable && price?.totalAmountPayable < 0) {
			isReturnPremium = true;
			isCardPaymentAvailable = hasPaymentOptionOfType(mtaResponse.paymentOptions, [
				PaymentType.SINGLE_PAYMENT_PAYMENT_CARD_RP,
			]);
		} else {
			isCardPaymentAvailable = hasPaymentOptionOfType(mtaResponse.paymentOptions, [
				PaymentType.SINGLE_PAYMENT_PAYMENT_CARD_AP,
			]);
		}
		isFinanceAvailable = hasPaymentOptionOfType(mtaResponse.paymentOptions, [
			PaymentType.INSTALMENT_ADJUSTMENT_SPREAD_EVENLY,
			PaymentType.INSTALMENT_ADJUSTMENT_NEXT_INSTALMENT,
		]);

		dispatch(
			setRoutingOptions({
				isReturnPremium,
				isCardPaymentAvailable,
				isFinanceAvailable,
			}),
		);

		navigate(ROUTE_PATHS.makeAChangePaymentOptions);
	};

	const isTaxi = fullPolicy.product === Product.TAXI;
	const isTelematicsProduct = isTelematics(fullPolicy.product);

	const confirmationPolicyWording = isTelematicsProduct
		? 'Please note that your Mid Term Adjustment has been quoted on the basis that your policy still includes a telematics device. Be aware any changes you make to your policy may influence your policy terms, such as key endorsements and excesses on the policy. If you go ahead with the change this will be communicated to you in your policy documents.'
		: 'Please be aware any changes you make to your policy may influence your policy terms, such as key endorsements and excesses on the policy. If you go ahead with the change this will be communicated to you in your policy documents.';
	return (
		<JourneyCardLayout pageTitle='Change Summary' loading={discardLoading}>
			<Grid display='flex' flexDirection='column' sx={{ alignItems: { sm: 'center' } }}>
				<Grid container flexDirection='column' sx={{ pb: 4, maxWidth: '700px', width: '100%' }}>
					{isTelematicsProduct && <PricingGuidanceAlert sx={{ mb: 2 }} />}
					<SummaryPrice
						mtaStartDate={state.mtaStartDate}
						totalAmountPayable={price?.totalAmountPayable}
						fee={mtaResponse.coverDetails.fee}
						product={fullPolicy.product}
					/>
					<SummaryCard
						title='Vehicle'
						isUpdated={state.changeOfVehicle}
						showAmendButton={
							state.isVehicleEligible && state.vehicleDetails.registration === fullPolicy.vehicleDetails.registration
						}
						amendLink={ROUTE_PATHS.makeAChangeVehicle}
					>
						<Vehicle
							isSummaryScreen
							product={fullPolicy.product}
							vehicle={{
								...state.vehicleDetails,
								registrationNumber: state.vehicleDetails.registration,
								vehicleUse: '',
							}}
						>
							<Typography>Estimated value: {formatPounds(state.vehicleDetails.value)}</Typography>
							<Typography sx={{ display: 'flex', alignItems: 'start' }}>
								{/* <VerifiedUser sx={{ marginRight: 1 }} />{" "} */}
								{getReferenceDataValue(ListNames.levelOfCoverTypes, state.vehicleDetails.coverDefinitionCode ?? '')}
							</Typography>
						</Vehicle>
					</SummaryCard>
					<SummaryCard title='Address' amendLink={ROUTE_PATHS.makeAChangeAddress} isUpdated={state.changeOfAddress}>
						<AddressDisplay
							address={state.policyDetails.policyholder.address}
							overnightAddress={state.vehicleDetails.overnightAddress ?? undefined}
						/>
					</SummaryCard>
					<SummaryCard title='Drivers' amendLink={ROUTE_PATHS.makeAChangeDrivers} isUpdated={state.changeOfDriver}>
						<Drivers
							drivers={state.drivers.map((driver) => ({
								...driver,
								dataPointId: driver.dataPointId ?? '',
								lastName: driver.lastNames,
								isPolicyholder: false,
								mainDriver: driver.isMainDriver,
							}))}
						/>
					</SummaryCard>
					<SummaryCard
						title={isTaxi ? 'Usage and Operator' : 'Usage and Mileage'}
						amendLink={isTaxi ? ROUTE_PATHS.makeAChangeUsageTaxi : ROUTE_PATHS.makeAChangeUsageAndMileage}
						isUpdated={state.changeOfUsage}
					>
						<UsageAndMilage
							vehicleUse={state.vehicleDetails.vehicleUse}
							annualMileage={state.vehicleDetails.annualMileage}
							businessMileage={state.vehicleDetails.businessMileage}
							vehicleCover={state.vehicleDetails.coverDefinitionCode}
							licencingAuthority={state.vehicleDetails.licencingAuthority}
							operatingArea={state.policyDetails.operatingArea}
							product={fullPolicy.product}
						/>
					</SummaryCard>
					{state.vehicleDetails.coverDefinitionCode !== 'TPO' && (
						<>
							<Box sx={{ width: '100%', py: 2 }}>
								<Typography fontWeight='bold'>Policy Excess</Typography>

								<Grid container alignItems='center' sx={{ py: 2 }} gap={2}>
									<Grid flex={1}>
										<Typography>
											{!!mtaResponse.coverDetails.voluntaryExcess && (
												<Price value={mtaResponse.coverDetails.voluntaryExcess} description='Voluntary Excess' />
											)}
										</Typography>
									</Grid>
									<Grid>
										<Price value={mtaResponse.coverDetails.compulsoryExcess} description='Compulsory Excess' />
									</Grid>
								</Grid>
							</Box>
							<DriverExcess drivers={mtaResponse.drivers} driverExcesses={mtaResponse.coverDetails.driverExcesses} />
							<Divider sx={{ mb: 4 }} />
						</>
					)}

					<Grid>
						<Endorsements endorsements={mtaResponse.coverDetails.endorsements || []} />
					</Grid>
					<Divider />
					<Box
						sx={(theme) => ({
							mx: 0,
							my: 2,
							[theme.breakpoints.down('md')]: { mx: 'unset' },
						})}
					>
						<CopyText>{confirmationPolicyWording}</CopyText>
						<PostalDocuments />
						<FormControlLabel
							control={
								<Checkbox
									id='confirm'
									name='confirm'
									slotProps={{
										input: {
											'aria-label': 'confirm',
										},
									}}
									icon={<CircleOutlined />}
									checkedIcon={<CheckCircle />}
									sx={{ '& .MuiSvgIcon-root': { fontSize: '3rem' } }}
									value={confirmChanges}
									onChange={() => setConfirmChanges(!confirmChanges)}
								/>
							}
							labelPlacement='start'
							label='I confirm all the details above are correct.'
							sx={{ margin: 0 }}
						/>
					</Box>
				</Grid>
			</Grid>

			<ActionButtons>
				<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
				<ContinueButton onClick={handleRoutingToPaymentOptions} disabled={!confirmChanges || MtaChangesMade}>
					Continue
				</ContinueButton>
			</ActionButtons>
			<HelperModal
				open={open}
				onClose={() => {
					toggleModal(false);
				}}
				title='Discard The Changes?'
				description='Are you sure you wish to discard the changes?'
				closeText='No'
				submitText='Yes'
				onSubmit={() => {
					toggleModal(false);
					handleDiscardMta();
				}}
			/>
		</JourneyCardLayout>
	);
};

export default MtaSummaryScreenPage;
